import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useUserRole } from "@/hooks/useUserRole";

const ACTIVE_ORDER_STATUSES = ["booked", "confirmed", "in_transit", "tender_received"] as const;

export interface ConflictWindow {
  start?: string | null;
  end?: string | null;
}

interface UseTractorConflictIdsArgs extends ConflictWindow {
  tenantId?: string | null;
  enabled?: boolean;
  /** Exclude this row from public.orders conflict checks. */
  excludeOrderId?: string | null;
  /** Exclude this row from public.legs conflict checks. */
  excludeLegId?: string | null;
  /** Exclude all leg rows for this order, used by parent-level order pickers. */
  excludeLegOrderId?: string | null;
}

interface UseTractorConflictIdsByWindowsArgs {
  tenantId?: string | null;
  /** One window per leg (or a single order-level window). */
  windows: ConflictWindow[];
  enabled?: boolean;
  excludeOrderId?: string | null;
  excludeLegId?: string | null;
  excludeLegOrderId?: string | null;
}

const normalizeInstant = (value?: string | null) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

interface TractorIdentityRow {
  id: string;
  name: string | null;
  vin: string | null;
  samsara_id: string | null;
}

interface AssignmentRow {
  tractorId: string;
  start: string;
  end: string;
}

const overlaps = (row: AssignmentRow, start: string, end: string) =>
  row.start < end && row.end > start;

export interface TractorConflictSets {
  /** Booked tractor ids for each requested window, in the same order. */
  perWindow: Set<string>[];
  /** Union of every window — used when one truck must cover the whole order. */
  union: Set<string>;
}

/**
 * Fetches every active tractor assignment overlapping the union of the
 * requested windows, then resolves a booked-id set for each window
 * individually. A conflict on leg 1 must not block a truck on leg 2.
 */
async function fetchConflictSets(args: {
  tenantId: string;
  windows: Array<{ start: string; end: string }>;
  excludeOrderId?: string | null;
  excludeLegId?: string | null;
  excludeLegOrderId?: string | null;
}): Promise<TractorConflictSets> {
  const { tenantId, windows, excludeOrderId, excludeLegId, excludeLegOrderId } = args;

  const unionStart = windows.reduce((min, w) => (w.start < min ? w.start : min), windows[0].start);
  const unionEnd = windows.reduce((max, w) => (w.end > max ? w.end : max), windows[0].end);

  let ordersQuery = supabase
    .from("orders")
    .select("id, assigned_tractor_id, ready_time, requested_delivery_time, requested_delivery_time_end")
    .eq("tenant_id", tenantId)
    .in("status", [...ACTIVE_ORDER_STATUSES])
    .not("assigned_tractor_id", "is", null)
    .lt("ready_time", unionEnd)
    .or(
      `requested_delivery_time_end.gt.${unionStart},and(requested_delivery_time_end.is.null,requested_delivery_time.gt.${unionStart})`,
    );

  if (excludeOrderId) ordersQuery = ordersQuery.neq("id", excludeOrderId);

  let legsQuery = supabase
    .from("legs")
    .select("id, order_id, assigned_tractor_id, driver_status, ready_time, requested_delivery_time, requested_delivery_time_end")
    .eq("tenant_id", tenantId)
    .not("assigned_tractor_id", "is", null)
    .lt("ready_time", unionEnd)
    .or(
      `requested_delivery_time_end.gt.${unionStart},and(requested_delivery_time_end.is.null,requested_delivery_time.gt.${unionStart})`,
    );

  if (excludeLegId) legsQuery = legsQuery.neq("id", excludeLegId);
  if (excludeLegOrderId) legsQuery = legsQuery.neq("order_id", excludeLegOrderId);

  const [ordersRes, legsRes] = await Promise.all([ordersQuery, legsQuery]);
  if (ordersRes.error) throw ordersRes.error;
  if (legsRes.error) throw legsRes.error;

  type TimedRow = {
    id?: string;
    order_id?: string | null;
    assigned_tractor_id: string | null;
    ready_time: string | null;
    requested_delivery_time: string | null;
    requested_delivery_time_end: string | null;
  };

  const orderRows = (ordersRes.data ?? []) as TimedRow[];
  // A leg that is delivered or cancelled has released its truck, even while
  // the parent order is still active on a later leg (AOP-HTSH-06113).
  const legRows = ((legsRes.data ?? []) as Array<TimedRow & { driver_status?: string | null }>).filter(
    (row) => row.driver_status !== "delivered" && row.driver_status !== "canceled",
  );

  const toAssignment = (row: TimedRow): AssignmentRow | null => {
    const start = normalizeInstant(row.ready_time);
    const end = normalizeInstant(row.requested_delivery_time_end || row.requested_delivery_time);
    if (!start || !end || !row.assigned_tractor_id) return null;
    return { tractorId: row.assigned_tractor_id, start, end };
  };

  const assignments: AssignmentRow[] = [];

  // `legs` is the authoritative asset assignment for any order that has leg
  // rows; the parent `orders.assigned_tractor_id` is only a leg-1 mirror and
  // can drift stale. Ignore the parent row whenever the order has legs.
  const orderIdsWithTractor = orderRows
    .filter((row) => !!row.assigned_tractor_id && !!row.id)
    .map((row) => row.id as string);
  const orderIdsWithLegs = new Set<string>();
  if (orderIdsWithTractor.length > 0) {
    const { data: legOwners, error: legOwnerErr } = await supabase
      .from("legs")
      .select("order_id")
      .eq("tenant_id", tenantId)
      .in("order_id", orderIdsWithTractor);
    if (legOwnerErr) throw legOwnerErr;
    (legOwners ?? []).forEach((row) => {
      if (row.order_id) orderIdsWithLegs.add(row.order_id as string);
    });
  }

  orderRows.forEach((row) => {
    if (row.id && orderIdsWithLegs.has(row.id)) return;
    const assignment = toAssignment(row);
    if (assignment) assignments.push(assignment);
  });

  // Leg assignments only count while their parent order is still active.
  const parentOrderIds = Array.from(
    new Set(legRows.map((row) => row.order_id).filter((value): value is string => !!value)),
  );
  if (parentOrderIds.length > 0) {
    const { data: activeParents, error } = await supabase
      .from("orders")
      .select("id")
      .in("id", parentOrderIds)
      .eq("tenant_id", tenantId)
      .in("status", [...ACTIVE_ORDER_STATUSES]);
    if (error) throw error;
    const activeParentIds = new Set((activeParents ?? []).map((row) => row.id as string));
    legRows.forEach((row) => {
      if (!row.order_id || !activeParentIds.has(row.order_id)) return;
      const assignment = toAssignment(row);
      if (assignment) assignments.push(assignment);
    });
  }

  // Some imported tractor rows are duplicated by physical identity (same VIN,
  // Samsara ID, or name) with different UUIDs. If one copy is booked in a
  // window, every matching copy must render booked in that same window.
  const anyBooked = assignments.length > 0;
  let allTractors: TractorIdentityRow[] = [];
  if (anyBooked) {
    const { data: tractorRows, error } = await supabase
      .from("tractors")
      .select("id, name, vin, samsara_id");
    if (error) throw error;
    allTractors = (tractorRows ?? []) as TractorIdentityRow[];
  }

  const expandIdentities = (ids: Set<string>) => {
    if (ids.size === 0 || allTractors.length === 0) return ids;
    const booked = allTractors.filter((tractor) => ids.has(tractor.id));
    const vins = new Set(booked.map((t) => t.vin).filter(Boolean));
    const samsaraIds = new Set(booked.map((t) => t.samsara_id).filter(Boolean));
    const names = new Set(booked.map((t) => t.name).filter(Boolean));
    allTractors.forEach((tractor) => {
      if (
        (tractor.vin && vins.has(tractor.vin)) ||
        (tractor.samsara_id && samsaraIds.has(tractor.samsara_id)) ||
        (tractor.name && names.has(tractor.name))
      ) {
        ids.add(tractor.id);
      }
    });
    return ids;
  };

  const perWindow = windows.map((window) => {
    const ids = new Set<string>();
    assignments.forEach((assignment) => {
      if (overlaps(assignment, window.start, window.end)) ids.add(assignment.tractorId);
    });
    return expandIdentities(ids);
  });

  const union = new Set<string>();
  perWindow.forEach((set) => set.forEach((id) => union.add(id)));

  return { perWindow, union };
}

/**
 * Per-window booked lookup. Returns one set per requested window plus their
 * union, so per-leg tractor pickers can block only the legs that actually
 * overlap an existing assignment.
 */
export function useTractorConflictIdsByWindows({
  tenantId,
  windows,
  enabled = true,
  excludeOrderId,
  excludeLegId,
  excludeLegOrderId,
}: UseTractorConflictIdsByWindowsArgs) {
  const { isReadOnly } = useUserRole();

  const normalized = windows.map((w) => ({
    start: normalizeInstant(w.start),
    end: normalizeInstant(w.end),
  }));
  const resolvable = normalized.filter(
    (w): w is { start: string; end: string } => !!w.start && !!w.end,
  );

  const query = useQuery({
    queryKey: [
      "tractor-conflict-ids",
      "windows",
      tenantId ?? null,
      normalized,
      excludeOrderId ?? null,
      excludeLegId ?? null,
      excludeLegOrderId ?? null,
    ],
    enabled: enabled && !isReadOnly && !!tenantId && resolvable.length > 0,
    queryFn: () =>
      fetchConflictSets({
        tenantId: tenantId!,
        windows: resolvable,
        excludeOrderId,
        excludeLegId,
        excludeLegOrderId,
      }),
  });

  // Map results back onto the caller's window list (unresolvable windows get
  // an empty set so indexes stay aligned with the caller's legs).
  const perWindow: Set<string>[] = [];
  let cursor = 0;
  normalized.forEach((w) => {
    if (w.start && w.end) {
      perWindow.push(query.data?.perWindow[cursor] ?? new Set<string>());
      cursor += 1;
    } else {
      perWindow.push(new Set<string>());
    }
  });

  return {
    ...query,
    perWindow,
    union: query.data?.union ?? new Set<string>(),
  };
}

export function useTractorConflictIds({
  tenantId,
  start,
  end,
  enabled = true,
  excludeOrderId,
  excludeLegId,
  excludeLegOrderId,
}: UseTractorConflictIdsArgs) {
  // NOTE: hooks must be called in the same order on every render. Call
  // useUserRole() BEFORE any conditional short-circuits and before useQuery
  // so callers that conditionally mount this hook do not desync the hook
  // list inside OrderFormDialog.
  const { isReadOnly } = useUserRole();
  const normalizedStart = normalizeInstant(start);
  const normalizedEnd = normalizeInstant(end);

  return useQuery({
    queryKey: [
      "tractor-conflict-ids",
      tenantId ?? null,
      normalizedStart,
      normalizedEnd,
      excludeOrderId ?? null,
      excludeLegId ?? null,
      excludeLegOrderId ?? null,
    ],
    // Read-only users (analysts / domain-allowed) never assign tractors,
    // so the conflict lookup is pure overhead for them. Skip the query.
    enabled: enabled && !isReadOnly && !!tenantId && !!normalizedStart && !!normalizedEnd,
    queryFn: async () => {
      const { union } = await fetchConflictSets({
        tenantId: tenantId!,
        windows: [{ start: normalizedStart!, end: normalizedEnd! }],
        excludeOrderId,
        excludeLegId,
        excludeLegOrderId,
      });
      return union;
    },
  });
}
